import { cn } from '@skupreme/shared/ui';
import { workspaceInitials } from '@utility/own-carrier-account';
import { useEffect, useRef, useState } from 'react';
import { Button, Tooltip, TooltipTrigger } from 'react-aria-components';

export type OwnCarrierAccountMarkProps = {
  /** Shown name of the carrier, e.g. "FedEx". */
  carrierName: string;
  /** Only the last four digits; the full number never reaches the page. */
  last4: string;
  workspaceName?: string;
  workspaceLogo?: string;
  className?: string;
};

/**
 * The workspace's own icon beside a carrier, saying the rate or account is the
 * workspace's own. Hover, keyboard focus and a tap all show which account.
 */
export function OwnCarrierAccountMark({
  carrierName,
  last4,
  workspaceName,
  workspaceLogo,
  className,
}: OwnCarrierAccountMarkProps) {
  const [isOpen, setOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const label = `Your ${carrierName} account ••••${last4}`;
  const showLogo = !!workspaceLogo && !logoFailed;

  /* A tooltip opened by a tap has no pointer to leave it, so a tap anywhere
     else closes it. */
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const closeOutside = (event: PointerEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOutside);

    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [isOpen]);

  return (
    /* React Aria's trigger directly: the open state has to be held here so a
       tap can open it, and the kit Tooltip does not take it on every branch. */
    <TooltipTrigger
      delay={100}
      closeDelay={300}
      isOpen={isOpen}
      onOpenChange={setOpen}
    >
      <Button
        ref={triggerRef}
        aria-label={label}
        data-testid="own-carrier-account-mark"
        /* Hover and focus open it on their own; a tap has neither, so a tap
           toggles it. */
        onPress={event =>
          setOpen(open => (event.pointerType === 'touch' ? !open : true))
        }
        className={cn(
          'sk:inline-flex sk:size-4 sk:shrink-0 sk:cursor-default sk:items-center',
          'sk:justify-center sk:overflow-hidden sk:rounded-full sk:outline-none',
          'sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid',
          'sk:rac-focus-visible:outline-primary sk:rac-focus-visible:outline-offset-1',
          showLogo ? '' : 'sk:bg-fill-secondary sk:text-[8px] sk:font-semibold',
          className,
        )}
      >
        {showLogo ? (
          <img
            src={workspaceLogo}
            alt=""
            className="sk:size-full sk:object-cover"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <span aria-hidden="true">{workspaceInitials(workspaceName)}</span>
        )}
      </Button>
      <Tooltip
        placement="top"
        offset={6}
        className={cn(
          'sk:z-3000 sk:max-w-96 sk:rounded-card sk:px-3 sk:py-2',
          'sk:inset-shadow-hairline sk:bg-surface sk:shadow-card',
          'sk:text-dense sk:leading-5 sk:text-fg sk:outline-none',
        )}
      >
        {label}
      </Tooltip>
    </TooltipTrigger>
  );
}
